import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDisplayDate } from '../components/DateFilter';

// Item #4: every date in every export in DD-MM-YYYY. Rather than touching
// every column definition across the app, this detects ISO-date-shaped
// values ('YYYY-MM-DD', optionally with a time/zone suffix) as they're
// stringified for export and reformats just those — anything else (names,
// companies, free text) passes through unchanged.
function formatCellForExport(val) {
    if (val === null || val === undefined) return '';
    const str = String(val);
    // Only ISO-date-shaped strings are touched; names/companies/free text pass through.
    if (!/^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(str)) return str;
    return formatDisplayDate(str);
}

// --- Shared: timestamped filenames ------------------------------------------

function timestampSuffix() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
}

// --- CSV export -------------------------------------------------------------

export function exportToCSV(rows, columns, filename) {
    // S.No is always the first column; it numbers the rows being exported (i.e. the filtered view).
    const header = ['"S.No"', ...columns.map(c => `"${c.label}"`)].join(',');
    const csvRows = rows.map((row, i) =>
        [`"${i + 1}"`, ...columns.map(c => {
            const str = formatCellForExport(row[c.key]);
            return `"${str.replace(/"/g, '""')}"`;
        })].join(',')
    );
    const csvContent = [header, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${timestampSuffix()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// --- PDF export ---------------------------------------------------------

// Loads an image and returns its data URL plus natural width/height, so the
// watermark can be drawn at the logo's correct aspect ratio instead of being
// stretched or squashed.
function loadImageAsDataURL(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.width, height: img.height });
        };
        img.onerror = reject;
        img.src = src;
    });
}

export async function exportToPDF(rows, columns, filename, title) {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Logo is optional — if it can't be loaded, the PDF still generates fine,
    // just without the header logo or the watermark.
    let logo = null;
    try {
        logo = await loadImageAsDataURL('/assets/logo.png');
    } catch (e) {
        console.warn('Company logo not found at /assets/logo.png — exporting without it.');
    }

    const drawHeaderAndWatermark = () => {
        if (logo) {
            // Header logo, top-left, normal opacity
            doc.addImage(logo.dataUrl, 'PNG', 30, 18, 90, 90 * (logo.height / logo.width));

            // Watermark — the logo itself, large, centered, faded, behind the table
            const wmWidth = 260;
            const wmHeight = wmWidth * (logo.height / logo.width);
            doc.saveGraphicsState();
            doc.setGState(new doc.GState({ opacity: 0.08 }));
            doc.addImage(logo.dataUrl, 'PNG', pageWidth / 2 - wmWidth / 2, pageHeight / 2 - wmHeight / 2, wmWidth, wmHeight);
            doc.restoreGraphicsState();
        }

        doc.setFontSize(13);
        doc.setTextColor(15, 23, 42);
        doc.text(title, pageWidth / 2, 34, { align: 'center' });
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(`Exported ${new Date().toLocaleString()}`, pageWidth - 30, 34, { align: 'right' });
    };

    drawHeaderAndWatermark();

    autoTable(doc, {
        startY: 55,
        head: [['S.No', ...columns.map(c => c.label)]],
        body: rows.map((row, i) => [String(i + 1), ...columns.map(c => formatCellForExport(row[c.key]))]),
        styles: { fontSize: 6.5, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 7 },
        didDrawPage: drawHeaderAndWatermark,
        margin: { top: 55 }
    });

    doc.save(`${filename}_${timestampSuffix()}.pdf`);
}